import { useQuery } from "@tanstack/react-query";

import { UserSettingKey, userSettingsApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { QueryKey } from "@/query/Query";

const userSettingsQueryOptions = {
  queryKey: [QueryKey.UserSettings],
  queryFn: () => userSettingsApi.getUserSettings(withLocalErrorHandling),
  // Only this app writes these settings, and it updates the cache itself when it does.
  staleTime: Infinity,
};

/**
 * The settings the signed-in user has changed. Keys the user never touched are absent from the
 * response and fall back to their default, so a failed request degrades to the defaults rather than
 * blocking the page it is read on.
 */
export function useUserSettings() {
  const { data: settings, isLoading } = useQuery(userSettingsQueryOptions);

  const valuesByKey = new Map((settings ?? []).map((setting) => [setting.key, setting.value]));
  const isClientFileColorCodingOn = valuesByKey.get(UserSettingKey.ClientFileColorCoding) === true;

  return { isLoading, isClientFileColorCodingOn };
}

/**
 * Starts the request once per session from above the router, so a page that reads a setting has it
 * on first paint instead of repainting when it arrives. Nothing waits for it — a page that never
 * gets an answer simply keeps the defaults.
 */
export function useWarmUserSettings() {
  useQuery(userSettingsQueryOptions);
}
